import React, { useState, useMemo, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Modal } from "@/components/Modal";
import { Button } from "@/components/Button";
import { Icon } from "@/components/Icon";
import { Switch } from "@/components/Switch";
import { useToast } from "@/context/ToastContext";
import { tenderApi } from "@/lib/api";

interface TenderMigrationModalProps {
    isOpen: boolean;
    onClose: () => void;
    onMigrationSuccess?: () => void;
}

interface MigrationStats {
    clients_created: number;
    clients_skipped: number;
    tenders_created: number;
    tenders_skipped: number;
    studies_created?: number;
    studies_skipped?: number;
    total_clients: number;
    total_tenders: number;
    total_studies?: number;
    errors: string[];
    scope?: string;
    scope_message?: string;
    databases?: string[];
}

export const TenderMigrationModal: React.FC<TenderMigrationModalProps> = ({
    isOpen,
    onClose,
    onMigrationSuccess,
}) => {
    const { t } = useTranslation("global");
    const { showSuccess, showError } = useToast();

    const [testing, setTesting] = useState<boolean>(false);
    const [migrating, setMigrating] = useState<boolean>(false);
    const [connectionResult, setConnectionResult] = useState<{
        success: boolean;
        message: string;
        tenders_count?: number;
        studies_count?: number;
    } | null>(null);
    const [includeHistorical, setIncludeHistorical] = useState<boolean>(true);
    const [stats, setStats] = useState<MigrationStats | null>(null);
    const statsEndRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (stats && statsEndRef.current) {
            statsEndRef.current.scrollIntoView({
                behavior: "smooth",
                block: "end",
            });
        }
    }, [stats]);

    // Resolve whether we are in main context or a specific company
    const activeCompanyId = useMemo<string | null>(() => {
        const stored = localStorage.getItem("vigepra_active_company");
        return stored && stored !== "main" ? stored : null;
    }, [isOpen]);

    const isMainContext = activeCompanyId === null;

    const handleTestConnection = async (): Promise<void> => {
        setTesting(true);
        setConnectionResult(null);
        try {
            const res = await tenderApi.testMigration();
            const connData =
                (
                    res as {
                        data?: {
                            success: boolean;
                            message: string;
                            tenders_count?: number;
                            studies_count?: number;
                        };
                    }
                )?.data || res;
            if (connData) {
                setConnectionResult(connData);
                if (connData.success) {
                    showSuccess(t("tender.connection_ok"));
                } else {
                    showError(connData.message || t("tender.connection_error"));
                }
            }
        } catch (error: unknown) {
            console.error(error);
            const err = error as {
                response?: {
                    data?: { message?: string; data?: { message?: string } };
                };
                message?: string;
            };
            const errorMsg =
                err?.response?.data?.message ||
                err?.response?.data?.data?.message ||
                err?.message ||
                t("tender.connection_error");
            showError(errorMsg);
            setConnectionResult({
                success: false,
                message: errorMsg,
            });
        } finally {
            setTesting(false);
        }
    };

    const handleRunMigration = async (): Promise<void> => {
        setMigrating(true);
        setStats(null);
        try {
            const res = await tenderApi.runMigration(includeHistorical);
            if (res?.data) {
                setStats(res.data);
                showSuccess(t("tender.migration_success"));
                onMigrationSuccess?.();
            }
        } catch (error: unknown) {
            console.error(error);
            const err = error as {
                response?: {
                    data?: {
                        message?: string;
                        errors?: Record<string, unknown>;
                    };
                };
                message?: string;
            };
            const errorMsg =
                err?.response?.data?.message ||
                (err?.response?.data?.errors
                    ? Object.values(err.response.data.errors).flat().join(" ")
                    : "") ||
                err?.message ||
                t("tender.migration_error");

            showError(errorMsg);
            setStats({
                clients_created: 0,
                clients_skipped: 0,
                tenders_created: 0,
                tenders_skipped: 0,
                total_clients: 0,
                total_tenders: 0,
                errors: [errorMsg],
            });
        } finally {
            setMigrating(false);
        }
    };

    const handleHistoricalChange = (checked: boolean): void => {
        setIncludeHistorical(checked);
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={t("tender.migration_title")}
            maxWidth="xl"
            cancelLabel={t("tender.close")}
            isSubmitting={migrating}
            onSubmit={handleRunMigration}
            submitLabel={t("tender.run_migration")}
            submittingLabel={t("tender.migrating")}
            submitDisabled={testing}
        >
            <div className="flex flex-col items-center justify-center p-2 space-y-6">
                <div className="flex flex-col items-center justify-center space-y-3 text-center">
                    <Icon
                        value="database"
                        size={32}
                        color="primary"
                        background
                        className="mb-2"
                    />
                    <p className="text-sm text-slate-600 dark:text-zinc-300 text-center">
                        {t("tender.migration_desc")}
                    </p>
                </div>

                {/* Scope notice: shows if migrating all or just one company */}
                <div
                    className={`w-full rounded-xl border p-4 text-center flex flex-col items-center justify-center gap-1 ${
                        isMainContext
                            ? "border-amber-200 bg-amber-50/60 dark:border-amber-900/50 dark:bg-amber-950/20"
                            : "border-sky-200 bg-sky-50/60 dark:border-sky-900/50 dark:bg-sky-950/20"
                    }`}
                >
                    <Icon
                        value={isMainContext ? "globe" : "building"}
                        size={18}
                        className={
                            isMainContext ? "text-amber-600" : "text-sky-600"
                        }
                    />
                    <p
                        className={`text-xs font-semibold mt-1 ${
                            isMainContext
                                ? "text-amber-900 dark:text-amber-200"
                                : "text-sky-900 dark:text-sky-200"
                        }`}
                    >
                        {isMainContext
                            ? t("tools.tender_migration.scope_all_title")
                            : t("tools.tender_migration.scope_company_title")}
                    </p>
                    <p
                        className={`text-xs leading-relaxed ${
                            isMainContext
                                ? "text-amber-700 dark:text-amber-300"
                                : "text-sky-700 dark:text-sky-300"
                        }`}
                    >
                        {isMainContext
                            ? t("tools.tender_migration.scope_all_desc")
                            : t("tools.tender_migration.scope_company_desc")}
                    </p>
                </div>

                <div className="w-full rounded-xl border border-blue-200 bg-blue-50/60 p-4 dark:border-blue-900/50 dark:bg-blue-950/20 text-center flex flex-col items-center justify-center">
                    <Icon
                        value="checkCircle"
                        size={20}
                        className="text-blue-600 mb-1"
                    />
                    <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-200 text-center">
                        {t("tools.tender_migration.duplicate_notice_title")}
                    </h4>
                    <p className="text-xs text-blue-700 dark:text-blue-300 mt-1 leading-relaxed text-center">
                        {t("tools.tender_migration.duplicate_notice_desc")}
                    </p>
                </div>

                <div className="w-full flex flex-col items-center justify-center text-center gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4 dark:border-white/10 dark:bg-dark-card">
                    <div>
                        <p className="text-sm font-semibold text-slate-800 dark:text-zinc-200">
                            {t("tender.test_connection")}
                        </p>
                        <p className="text-xs text-slate-500 mt-0.5">
                            {t("tools.tender_migration.ssh_config_hint")}
                        </p>
                    </div>

                    <Button
                        variant="secondary"
                        onClick={handleTestConnection}
                        disabled={testing || migrating}
                    >
                        <div className="flex items-center gap-2">
                            <Icon
                                value={testing ? "refresh" : "check"}
                                size={15}
                                className={testing ? "animate-spin" : ""}
                            />
                            <span>
                                {testing
                                    ? t("common.loading")
                                    : t("tender.test_connection")}
                            </span>
                        </div>
                    </Button>

                    {connectionResult && (
                        <div
                            className={`w-full p-4 rounded-xl border text-sm flex flex-col items-center justify-center text-center gap-2 ${
                                connectionResult.success
                                    ? "bg-emerald-50 text-emerald-800 border-emerald-200 dark:bg-emerald-950/20 dark:border-emerald-800/40 dark:text-emerald-300"
                                    : "bg-rose-50 text-rose-800 border-rose-200 dark:bg-rose-950/20 dark:border-rose-800/40 dark:text-rose-300"
                            }`}
                        >
                            <Icon
                                value={
                                    connectionResult.success
                                        ? "checkCircle"
                                        : "alertCircle"
                                }
                                size={22}
                                className="shrink-0"
                            />
                            <div className="w-full">
                                <p className="font-semibold text-center break-words">
                                    {connectionResult.message}
                                </p>
                                {connectionResult.tenders_count !==
                                    undefined && (
                                    <p className="text-xs mt-1 text-center font-medium">
                                        {t(
                                            "tools.tender_migration.tenders_found",
                                            {
                                                count: connectionResult.tenders_count,
                                            }
                                        )}
                                    </p>
                                )}
                                {connectionResult.studies_count !==
                                    undefined && (
                                    <p className="text-xs mt-0.5 text-center font-medium">
                                        {t("studies.migration_found_studies", {
                                            count: connectionResult.studies_count,
                                        })}
                                    </p>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                <div className="w-full rounded-xl border border-slate-200 p-4 dark:border-white/10 flex flex-col items-center justify-center">
                    <Switch
                        checked={includeHistorical}
                        onChange={handleHistoricalChange}
                        label={t("tender.include_historical")}
                        description={t("tender.include_historical_desc")}
                        disabled={migrating}
                        size="md"
                        className="w-full"
                    />
                </div>

                {stats && (
                    <div className="w-full space-y-3 rounded-xl border border-slate-200 bg-slate-50 p-4 dark:border-white/10 dark:bg-dark-card text-sm text-center">
                        <div className="flex items-center justify-center gap-2">
                            <Icon
                                value="checkCircle"
                                size={16}
                                className="text-emerald-600"
                            />
                            <span className="font-semibold text-slate-900 dark:text-white">
                                {t("tender.migration_success")}
                            </span>
                        </div>

                        {/* Scope message returned by backend */}
                        {stats.scope_message && (
                            <p className="text-xs text-slate-500 dark:text-zinc-400 italic">
                                {stats.scope_message}
                            </p>
                        )}

                        <div className="grid grid-cols-2 gap-3 text-xs">
                            <div className="p-3 bg-white dark:bg-dark-box rounded-lg border border-slate-200/80 dark:border-white/5 text-center">
                                <p className="text-slate-500 font-medium">
                                    {t("tender.block_client")}
                                </p>
                                <p className="text-sm font-semibold mt-1 text-slate-800 dark:text-zinc-200">
                                    {t(
                                        "tender.migration_stats_clients_created",
                                        { created: stats.clients_created }
                                    )}
                                </p>
                                <p className="text-sm font-semibold mt-1 text-slate-800 dark:text-zinc-200">
                                    {t(
                                        "tender.migration_stats_clients_skipped",
                                        { skipped: stats.clients_skipped }
                                    )}
                                </p>
                            </div>
                            <div className="p-3 bg-white dark:bg-dark-box rounded-lg border border-slate-200/80 dark:border-white/5 text-center">
                                <p className="text-slate-500 font-medium">
                                    {t("tender.title")}
                                </p>
                                <p className="text-sm font-semibold mt-1 text-slate-800 dark:text-zinc-200">
                                    {t(
                                        "tender.migration_stats_tenders_created",
                                        { created: stats.tenders_created }
                                    )}
                                </p>
                                <p className="text-sm font-semibold mt-1 text-slate-800 dark:text-zinc-200">
                                    {t(
                                        "tender.migration_stats_tenders_skipped",
                                        { skipped: stats.tenders_skipped }
                                    )}
                                </p>
                            </div>
                            {stats.total_studies !== undefined && (
                                <div className="p-3 bg-white dark:bg-dark-box rounded-lg border border-slate-200/80 dark:border-white/5 text-center col-span-2">
                                    <p className="text-slate-500 font-medium">
                                        {t("studies.title")}
                                    </p>
                                    <p className="text-sm font-semibold mt-1 text-slate-800 dark:text-zinc-200">
                                        {t("studies.migration_stats_created", {
                                            created: stats.studies_created ?? 0,
                                        })}
                                    </p>
                                    <p className="text-sm font-semibold mt-1 text-slate-800 dark:text-zinc-200">
                                        {t("studies.migration_stats_skipped", {
                                            skipped: stats.studies_skipped ?? 0,
                                        })}
                                    </p>
                                </div>
                            )}
                        </div>

                        {stats.errors?.length > 0 && (
                            <div className="mt-2 text-xs text-rose-600 space-y-1 text-center">
                                {stats.errors.map((err: string, i: number) => (
                                    <p key={i}>• {err}</p>
                                ))}
                            </div>
                        )}
                        <div ref={statsEndRef} />
                    </div>
                )}
            </div>
        </Modal>
    );
};
